import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FileText, Download, Calendar, MapPin, AlertTriangle, Users, ChevronRight, ChevronLeft, MessageCircle, X, BookOpen, Scale, Briefcase, Wallet, ClipboardList, FileCheck, Bell, Filter, XCircle, CalendarDays, Palmtree, LogIn, LogOut, User as UserIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Input } from '../components/ui/input';
import { AnimateIn } from '../components/Layout';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const indianStates = [
  "Central",
  // States (28)
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh",
  "Goa", "Gujarat", "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka",
  "Kerala", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya", "Mizoram",
  "Nagaland", "Odisha", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
  "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal",
  // Union Territories (8)
  "Andaman & Nicobar Islands", "Chandigarh", "Dadra & Nagar Haveli and Daman & Diu",
  "Delhi", "Jammu & Kashmir", "Ladakh", "Lakshadweep", "Puducherry",
];

const categoryIcons = {
  "Minimum Wages": Wallet,
  "Labour Code": BookOpen,
  "ESI": Users,
  "PF": Briefcase,
  "Professional Tax": FileCheck,
  "Registers": ClipboardList,
  "Returns": FileText,
  "Gazette Notifications": Bell,
  "POSH": AlertTriangle,
  "Leave": CalendarDays,
  "N&F Holidays": Palmtree,
};

const categories = [
  "Minimum Wages", "Labour Code", "ESI", "PF", "Professional Tax",
  "Registers", "Returns", "Gazette Notifications", "POSH", "Leave", "N&F Holidays",
];

// State Card Component - Simple card showing state name and effective date
const StateCard = ({ state, effectiveDate, onClick, idx }) => (
  <AnimateIn delay={idx * 40}>
    <button
      onClick={onClick}
      className="w-full p-5 border border-blue-100 rounded-lg bg-white hover:border-blue-300 hover:shadow-md transition-all duration-200 text-left group"
    >
      <h3 className="font-body font-semibold text-[#0B1F3A] text-base mb-2 group-hover:text-blue-600 transition-colors">
        {state}
      </h3>
      <p className="text-sm text-gray-500 font-body">
        Effective: {effectiveDate}
      </p>
    </button>
  </AnimateIn>
);

// Resource Detail Modal - Shows full details when state card is clicked
const ResourceDetailModal = ({ resource, onClose, formatDate, onDownloadClick }) => {
  const impactColors = {
    High: 'bg-red-50 text-red-600 border-red-200',
    Medium: 'bg-amber-50 text-amber-600 border-amber-200',
    Low: 'bg-green-50 text-green-600 border-green-200',
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white rounded-lg max-w-4xl w-full my-8 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-6 flex items-start justify-between">
          <div className="flex items-start gap-4 flex-1">
            <div className="w-11 h-11 rounded-sm bg-[#0B1F3A] flex items-center justify-center shrink-0">
              <FileText size={20} className="text-[#C9A646]" />
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-xl font-bold text-[#0B1F3A] mb-2">{resource.title}</h2>
              <div className="flex flex-wrap gap-x-5 gap-y-2 text-sm font-body text-gray-500">
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} className="text-[#C9A646]" />
                  Notified: {formatDate(resource.notification_date)}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} className="text-gray-300" />
                  Effective: {formatDate(resource.effective_date)}
                </span>
                {resource.state && (
                  <span className="flex items-center gap-1.5">
                    <MapPin size={14} className="text-[#C9A646]" />
                    {resource.state}
                  </span>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X size={20} className="text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="flex flex-wrap gap-2 mb-6">
            <span className="px-3 py-1 rounded-sm bg-[#0B1F3A]/5 text-[#0B1F3A] font-body text-xs font-semibold">
              {resource.act_law_name}
            </span>
            <span className="px-3 py-1 rounded-sm bg-[#C9A646]/10 text-[#C9A646] font-body text-xs font-semibold">
              {resource.applicability}
            </span>
            {resource.impact_level && (
              <span className={`px-3 py-1 rounded-sm font-body text-xs font-semibold border ${impactColors[resource.impact_level] || 'bg-gray-50 text-gray-600 border-gray-200'}`}>
                <AlertTriangle size={12} className="inline mr-1 -mt-0.5" />
                {resource.impact_level} Impact
              </span>
            )}
          </div>

          {resource.short_summary && (
            <div className="mb-6">
              <h4 className="font-body font-semibold text-sm text-[#0B1F3A] mb-2">Summary</h4>
              <p className="text-gray-600 font-body text-sm leading-relaxed">{resource.short_summary}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            {resource.key_changes && (
              <div>
                <h4 className="font-body font-semibold text-sm text-[#0B1F3A] mb-2 flex items-center gap-1.5">
                  <FileText size={14} className="text-[#C9A646]" /> Key Changes
                </h4>
                <p className="text-gray-600 font-body text-sm leading-relaxed whitespace-pre-line">{resource.key_changes}</p>
              </div>
            )}
            {resource.who_applies_to && (
              <div>
                <h4 className="font-body font-semibold text-sm text-[#0B1F3A] mb-2 flex items-center gap-1.5">
                  <Users size={14} className="text-[#C9A646]" /> Who It Applies To
                </h4>
                <p className="text-gray-600 font-body text-sm leading-relaxed">{resource.who_applies_to}</p>
              </div>
            )}
            {resource.action_required && (
              <div className="md:col-span-2">
                <h4 className="font-body font-semibold text-sm text-[#0B1F3A] mb-2 flex items-center gap-1.5">
                  <AlertTriangle size={14} className="text-[#C9A646]" /> Action Required
                </h4>
                <p className="text-gray-600 font-body text-sm leading-relaxed whitespace-pre-line">{resource.action_required}</p>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-3 pt-5 border-t border-gray-100">
            <Button
              onClick={() => onDownloadClick(resource)}
              className="bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white font-body rounded-sm h-10 px-6 text-sm w-full sm:w-auto"
            >
              <Download size={16} className="mr-2" /> Download Full Notification
            </Button>
            <Link to="/contact">
              <Button variant="outline" className="border-[#C9A646] text-[#C9A646] hover:bg-[#C9A646] hover:text-[#0B1F3A] font-body rounded-sm h-10 px-6 text-sm w-full sm:w-auto">
                <MessageCircle size={16} className="mr-2" /> Contact for Further Details
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const Resources = () => {
  const location = useLocation();
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("Minimum Wages");
  const [selectedResource, setSelectedResource] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  
  // User authentication state
  const [user, setUser] = useState(location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!location.state?.user);
  const [checkingAuth, setCheckingAuth] = useState(!location.state?.user);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  
  // Filter states
  const [selectedLocation, setSelectedLocation] = useState('');
  const [filterEffectiveDate, setFilterEffectiveDate] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterSubCategory, setFilterSubCategory] = useState('');
  const [filterType, setFilterType] = useState('');


  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      if (location.state?.user) {
        // User data passed from AuthCallback
        setCheckingAuth(false);
        return;
      }
      
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        setIsAuthenticated(true);
      } catch (err) {
        setIsAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, [location.state]);

  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/resources';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const handleDownloadClick = async (resource) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    
    // If authenticated, create download link (simpler approach)
    try {
      // Log download for analytics
      try {
        await axios.post(`${API}/downloads/log`, {
          resource_id: resource.id,
          resource_title: resource.title,
          state: resource.state,
          category: resource.category
        });
      } catch (err) {
        console.error('Failed to log download', err);
      }
      
      // Create download link and trigger
      const link = document.createElement('a');
      link.href = `${BACKEND_URL}${resource.pdf_url}`;
      link.setAttribute('download', resource.pdf_filename || 'document.pdf');
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Download error:', error);
      alert('Failed to download file. Please try again.');
    }
  };


  const fetchResources = React.useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory) params.append('category', selectedCategory);
      const res = await axios.get(`${API}/resources?${params.toString()}`);
      setResources(res.data);
    } catch (err) {
      console.error('Failed to fetch resources', err);
    } finally {
      setLoading(false);
    }
  }, [selectedCategory]);

  useEffect(() => {
    fetchResources();
  }, [fetchResources]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  // Apply filters to resources
  const filteredResources = React.useMemo(() => {
    return resources.filter(res => {
      // Location filter
      if (selectedLocation && selectedLocation !== 'all') {
        const resLocation = res.specific_location || res.state || 'All India';
        if (resLocation !== selectedLocation) return false;
      }
      
      // Effective Date filter (exact match)
      if (filterEffectiveDate && res.effective_date) {
        const resDate = new Date(res.effective_date).toISOString().split('T')[0];
        if (resDate !== filterEffectiveDate) return false;
      }
      
      // Category filter
      if (filterCategory && filterCategory !== 'all') {
        if (res.category !== filterCategory) return false;
      }
      
      // Sub-Category filter
      if (filterSubCategory && filterSubCategory !== 'all') {
        if (res.sub_category_1 !== filterSubCategory) return false;
      }
      
      // Type filter (sub_category_2)
      if (filterType && filterType !== 'all') {
        if (res.sub_category_2 !== filterType) return false;
      }
      
      return true;
    });
  }, [resources, selectedLocation, filterEffectiveDate, filterCategory, filterSubCategory, filterType]);

  // Group filtered resources by state
  const groupedByState = React.useMemo(() => {
    const grouped = {};
    filteredResources.forEach(res => {
      const state = res.state || 'All India';
      if (!grouped[state]) {
        grouped[state] = res;
      }
    });
    return grouped;
  }, [filteredResources]);

  const stateEntries = Object.entries(groupedByState).sort((a, b) => a[0].localeCompare(b[0]));

  const CategoryIcon = categoryIcons[selectedCategory] || FileText;
  
  // Get unique locations from all resources for filter dropdown
  const uniqueLocations = React.useMemo(() => {
    const locations = new Set();
    resources.forEach(res => {
      const location = res.specific_location || res.state || 'All India';
      locations.add(location);
    });
    return Array.from(locations).sort();
  }, [resources]);
  
  // Get unique categories
  const uniqueCategories = React.useMemo(() => {
    const cats = new Set();
    resources.forEach(res => {
      if (res.category) cats.add(res.category);
    });
    return Array.from(cats).sort();
  }, [resources]);
  
  // Get unique sub-categories (sub_category_1)
  const uniqueSubCategories = React.useMemo(() => {
    const subCats = new Set();
    resources.forEach(res => {
      if (res.sub_category_1) subCats.add(res.sub_category_1);
    });
    return Array.from(subCats).sort();
  }, [resources]);
  
  // Get unique types (sub_category_2)
  const uniqueTypes = React.useMemo(() => {
    const types = new Set();
    resources.forEach(res => {
      if (res.sub_category_2) types.add(res.sub_category_2);
    });
    return Array.from(types).sort();
  }, [resources]);
  
  // Clear all filters
  const clearFilters = () => {
    setSelectedLocation('');
    setFilterEffectiveDate('');
    setFilterCategory('');
    setFilterSubCategory('');
    setFilterType('');
  };
  
  // Check if any filter is active
  const hasActiveFilters = selectedLocation || filterEffectiveDate || filterCategory || filterSubCategory || filterType;

  return (
    <div className="pt-20 min-h-screen bg-white">
      {/* Two Column Layout */}
      <div className="flex">
        {/* Sidebar */}
        <aside className={`${sidebarCollapsed ? 'w-16' : 'w-64 lg:w-72'} bg-gray-50 border-r border-gray-200 min-h-screen sticky top-20 transition-all duration-300`}>
          {/* Collapse Toggle */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute -right-3 top-8 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors z-10"
          >
            {sidebarCollapsed ? <ChevronRight size={14} className="text-gray-600" /> : <ChevronLeft size={14} className="text-gray-600" />}
          </button>

          <div className="p-6">
            {!sidebarCollapsed && (
              <h2 className="font-heading text-lg font-bold text-[#0B1F3A] mb-6">Statutory Resources</h2>
            )}
            <nav className="space-y-1">
              {categories.map((category) => {
                const Icon = categoryIcons[category] || FileText;
                const isActive = selectedCategory === category;
                return (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-all duration-200 text-left ${
                      isActive
                        ? 'bg-blue-50 text-blue-600 font-semibold'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-[#0B1F3A]'
                    }`}
                    title={sidebarCollapsed ? category : undefined}
                  >
                    <Icon size={18} className={sidebarCollapsed ? 'mx-auto' : ''} />
                    {!sidebarCollapsed && <span className="font-body text-sm">{category}</span>}
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1">
          {/* Header Section */}
          <section className="py-12 md:py-16 bg-white border-b border-gray-100">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="flex items-start justify-between gap-4 mb-6">
                <AnimateIn className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
                      <CategoryIcon size={24} className="text-blue-600" />
                    </div>
                    <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0B1F3A]">
                      {selectedCategory} For States Across India
                    </h1>
                  </div>
                  <p className="text-gray-500 font-body text-base md:text-lg leading-relaxed max-w-3xl">
                    Access state-specific compliance information and updates. Click any state to view detailed notifications and requirements.
                  </p>
                </AnimateIn>
                
                {/* User Status */}
                <AnimateIn delay={100} className="flex-shrink-0">
                  {checkingAuth ? (
                    <div className="flex items-center gap-2 text-gray-400 text-sm">
                      <div className="w-4 h-4 border-2 border-gray-300 border-t-transparent rounded-full animate-spin" />
                      Checking...
                    </div>
                  ) : isAuthenticated ? (
                    <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-lg px-4 py-2">
                      <div className="flex items-center gap-2">
                        <UserIcon size={16} className="text-green-600" />
                        <span className="text-sm font-body font-semibold text-green-900">{user?.name}</span>
                      </div>
                      <button
                        onClick={handleLogout}
                        className="flex items-center gap-1 text-xs text-gray-600 hover:text-red-600 transition-colors"
                        title="Logout"
                      >
                        <LogOut size={14} />
                        Logout
                      </button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleLogin}
                      className="bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white font-body rounded-sm h-10 px-4 text-sm flex items-center gap-2"
                    >
                      <LogIn size={16} />
                      Login to Download
                    </Button>
                  )}
                </AnimateIn>
              </div>
            </div>
          </section>

          {/* Filters Section */}
          <section className="py-6 bg-gray-50 border-b border-gray-200">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              <div className="flex items-center gap-2 mb-4">
                <Filter size={16} className="text-gray-600" />
                <h3 className="font-body font-semibold text-sm text-gray-700">Filter Resources</h3>
                {hasActiveFilters && (
                  <button
                    onClick={clearFilters}
                    className="ml-auto flex items-center gap-1.5 text-xs font-body text-gray-500 hover:text-red-600 transition-colors"
                  >
                    <XCircle size={14} />
                    Clear Filters
                  </button>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Location Filter - Always visible */}
                <div>
                  <label className="text-xs font-body font-semibold text-gray-600 mb-1.5 block">Location</label>
                  <Select value={selectedLocation} onValueChange={setSelectedLocation}>
                    <SelectTrigger className="h-10 rounded-sm border-gray-300 font-body text-sm bg-white">
                      <SelectValue placeholder="All Locations" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60">
                      <SelectItem value="all">All Locations</SelectItem>
                      {uniqueLocations.map(loc => (
                        <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Effective Date - Always visible */}
                <div>
                  <label className="text-xs font-body font-semibold text-gray-600 mb-1.5 block">Effective Date</label>
                  <Input
                    type="date"
                    value={filterEffectiveDate}
                    onChange={(e) => setFilterEffectiveDate(e.target.value)}
                    className="h-10 rounded-sm border-gray-300 font-body text-sm"
                  />
                </div>

                {/* Category Filter - Hidden for Minimum Wages */}
                {selectedCategory !== "Minimum Wages" && (
                  <div>
                    <label className="text-xs font-body font-semibold text-gray-600 mb-1.5 block">Category</label>
                    <Select value={filterCategory} onValueChange={setFilterCategory}>
                      <SelectTrigger className="h-10 rounded-sm border-gray-300 font-body text-sm bg-white">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="all">All Categories</SelectItem>
                        {uniqueCategories.map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Sub-Category Filter - Hidden for Minimum Wages */}
                {selectedCategory !== "Minimum Wages" && (
                  <div>
                    <label className="text-xs font-body font-semibold text-gray-600 mb-1.5 block">Sub-Category</label>
                    <Select value={filterSubCategory} onValueChange={setFilterSubCategory}>
                      <SelectTrigger className="h-10 rounded-sm border-gray-300 font-body text-sm bg-white">
                        <SelectValue placeholder="All Sub-Categories" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="all">All Sub-Categories</SelectItem>
                        {uniqueSubCategories.map(subCat => (
                          <SelectItem key={subCat} value={subCat}>{subCat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Type Filter - Hidden for Minimum Wages */}
                {selectedCategory !== "Minimum Wages" && (
                  <div>
                    <label className="text-xs font-body font-semibold text-gray-600 mb-1.5 block">Type</label>
                    <Select value={filterType} onValueChange={setFilterType}>
                      <SelectTrigger className="h-10 rounded-sm border-gray-300 font-body text-sm bg-white">
                        <SelectValue placeholder="All Types" />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        <SelectItem value="all">All Types</SelectItem>
                        {uniqueTypes.map(type => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* State Cards Grid */}
          <section className="py-12 md:py-16 bg-white">
            <div className="max-w-7xl mx-auto px-6 md:px-12">
              {loading ? (
                <div className="text-center py-20">
                  <div className="w-8 h-8 border-2 border-[#C9A646] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                  <p className="text-gray-400 font-body">Loading resources...</p>
                </div>
              ) : stateEntries.length === 0 ? (
                <AnimateIn>
                  <div className="text-center py-20">
                    <FileText size={48} className="text-gray-200 mx-auto mb-6" />
                    <h3 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-3">No Resources Found</h3>
                    <p className="text-gray-400 font-body text-lg max-w-md mx-auto">
                      No resources available for this category yet. Check back soon!
                    </p>
                  </div>
                </AnimateIn>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-6">
                    <p className="text-gray-400 font-body text-sm">
                      Showing {stateEntries.length} state{stateEntries.length !== 1 ? 's' : ''} with {selectedCategory} updates
                      {hasActiveFilters && <span className="text-blue-600 ml-1">(filtered)</span>}
                    </p>
                    {hasActiveFilters && filteredResources.length !== resources.length && (
                      <p className="text-gray-400 font-body text-xs">
                        {filteredResources.length} of {resources.length} resources match filters
                      </p>
                    )}
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {stateEntries.map(([state, resource], idx) => (
                      <StateCard
                        key={state}
                        state={state}
                        effectiveDate={formatDate(resource.effective_date)}
                        onClick={() => setSelectedResource(resource)}
                        idx={idx}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          </section>
        </main>
      </div>

      {/* Resource Detail Modal */}
      {selectedResource && (
        <ResourceDetailModal
          resource={selectedResource}
          onClose={() => setSelectedResource(null)}
          formatDate={formatDate}
          onDownloadClick={handleDownloadClick}
        />
      )}

      {/* Login Prompt Modal */}
      {showLoginPrompt && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <LogIn size={32} className="text-blue-600" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-[#0B1F3A] mb-3">Login Required</h2>
              <p className="text-gray-600 font-body mb-6">
                Please login with your Google account to download resources and view complete details.
              </p>
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowLoginPrompt(false)}
                  variant="outline"
                  className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 font-body rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleLogin}
                  className="flex-1 bg-[#0B1F3A] hover:bg-[#0B1F3A]/90 text-white font-body rounded-sm"
                >
                  <LogIn size={16} className="mr-2" />
                  Login with Google
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Resources;
